import { useEffect } from 'react'
import {
  JsonParam,
  NumberParam,
  StringParam,
  ArrayParam,
  useQueryParams,
  withDefault,
} from 'use-query-params'
import { useGlobalState, useMessage, usePostBulkDocsDownload } from '@pepper/ui'
import { useModal } from '@ebay/nice-modal-react'

import DocList from 'components/items/DocList/DocList'
import AssignmentList from 'components/items/AssignmentList/AssignmentList'
import { sortOptions } from 'components/items/Header/schema'
import {
  setBulkDownloadFormLS,
  showBulkDocsDownloadForm,
} from 'container/Projects/LiveProjects/BulkDownloadModal/BulkDownloadModal.utils'
import BulkDownloadModal from 'container/Projects/LiveProjects/BulkDownloadModal/BulkDownloadModal'

import { useSelectedCompanyId } from 'utils/functions/misc'

import s from './AllItems.module.css'

const AllItems = () => {
  const { setTopbarComponent, permissions, setBulkDownloadJob } =
    useGlobalState()
  const { showError } = useMessage()
  const companyId = useSelectedCompanyId()
  const bulkDocsDownload = usePostBulkDocsDownload()
  const bulkDownloadModal = useModal(BulkDownloadModal)
  const [queryParamFilters] = useQueryParams({
    search_title: withDefault(StringParam, undefined),
    item_types: withDefault(StringParam, undefined),
    sort: withDefault(JsonParam, {
      sort_by: sortOptions[1].sortBy,
      sort_order: sortOptions[1].sortOrder,
    }),
    stage: withDefault(ArrayParam, undefined),
    rework: withDefault(StringParam, undefined),
    vertical: withDefault(ArrayParam, undefined),
    status: withDefault(ArrayParam, []),
    assignee: withDefault(ArrayParam, []),
    currentPage: withDefault(NumberParam, 0),
    tab: withDefault(StringParam, undefined),
  })

  useEffect(() => {
    setTopbarComponent(<div className={s.projectTitle}>Projects & Items</div>)

    return () => {
      setTopbarComponent(null)
    }
  }, [])

  const hasMarketplacePermission = permissions?.marketplace
  const showOrders =
    (hasMarketplacePermission && !queryParamFilters.item_types) ||
    queryParamFilters.item_types === 'assignment'

  const handleDocsDownload = (
    items: Item[] | Assignment[],
    itemType: 'assignment' | 'private_doc'
  ) => {
    if (showBulkDocsDownloadForm(companyId)) {
      bulkDownloadModal.show({
        handleSubmit: purpose => {
          downloadDocsMutation(items, purpose, itemType)
        },
      })
    } else {
      downloadDocsMutation(items, [], itemType)
    }
  }

  const downloadDocsMutation = (
    items: Item[] | Assignment[],
    purpose: {
      purpose: string
      message?: string
    }[],
    itemType: 'assignment' | 'private_doc'
  ) => {
    const projectName = 'Project'

    bulkDocsDownload.mutate(
      {
        companyId,
        body: {
          data: items.map(item => ({
            id: item.id,
            item_type: itemType,
          })),
          export_purpose: purpose,
        },
      },
      {
        onSuccess(data) {
          setBulkDownloadJob({
            id: data.id,
            name: projectName,
            companyId,
            count: items.length,
            status: 'pending',
          })
          setBulkDownloadFormLS({ companyId })
          bulkDownloadModal.hide()
        },
        onError(error) {
          if (error instanceof Error) {
            showError(error.message)
          }
        },
      }
    )
  }

  return (
    <div className={s.wrapper}>
      {showOrders ? (
        <AssignmentList
          viewType="all-items"
          handleDocsDownload={items => handleDocsDownload(items, 'assignment')}
        />
      ) : (
        <DocList
          viewType="all-items"
          handleDocsDownload={items => handleDocsDownload(items, 'private_doc')}
        />
      )}
    </div>
  )
}

export default AllItems
